<template>
  <div>
    <div class="screen-card-actions">
      <div v-if="label" class="existing-vote-info">
        <researcher-avatar-cluster
          :researchers="researchersToShow"
          :current-researcher="currentResearcher"
        />
        <template v-if="label">{{ label }}</template>
      </div>
      <div class="actions">
        <vote-controls
          v-if="showVoteButtons"
          :full-text-review="fullTextReview"
          @vote="onVoteButtonClick"
        />
        <assessment-dropdown
          v-else-if="showVoteDropdown"
          :value="yourVote"
          :hide-options="['UNSCREENED']"
          :show-revoke-option="allowRevoke"
          :inclusion-assessment="fullTextReview"
          @input="onVoteDropdownChange"
          @revoke="$emit('revoke-vote')"
        />
        <vote-controls
          v-else-if="showInclusionButtons"
          use-single-action-icons
          :full-text-review="fullTextReview"
          @vote="onVoteButtonClick"
        />
        <v-tooltip
          v-else-if="showInclusionDropdown"
          top
          :disabled="!disableChangingInclusionOutcome"
        >
          <template #activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <assessment-dropdown
                :value="outcome.outcome"
                :loading="outcome.isOptimistic()"
                :disabled="disableChangingInclusionOutcome"
                :hide-options="['UNSCREENED']"
                :show-revoke-option="allowRevoke"
                :inclusion-assessment="fullTextReview"
                show-outcome-icons
                @input="onVoteDropdownChange"
                @revoke="$emit('revoke-outcome')"
              />
            </div>
          </template>
          <span>
            {{ disableChangingInclusionOutcomeTooltip }}
          </span>
        </v-tooltip>
      </div>
    </div>
    <not-relevant-review-modal
      v-model="showExcludeModal"
      :review="review"
      @save="onExclude"
    />
  </div>
</template>

<script>
import ResearcherAvatarCluster from '@/components/Common/ResearcherAvatarCluster';
import VoteControls from '@/components/ScreenReferencesPage/VoteControls';
import AssessmentDropdown from '@/components/References/AssessmentDropdown';
import NotRelevantReviewModal from '@/components/FullTextReviewPage/NotRelevantReviewModal';
import { Reference, Researcher, Review } from '@/models';
import {
  ReferenceActionState,
  RelevanceAssessment,
  InclusionAssessment,
  ScreeningOutcomeResolution,
} from '@/enums';

const VOTE_CONTROLS_TO_RELEVANCE_ASSESSMENT = {
  YES: RelevanceAssessment.RELEVANT,
  NO: RelevanceAssessment.NOT_RELEVANT,
  NOT_SURE: RelevanceAssessment.NOT_SURE,
};

const VOTE_CONTROLS_TO_INCLUSION_ASSESSMENT = {
  YES: InclusionAssessment.INCLUDE,
  NO: InclusionAssessment.EXCLUDE,
};

const VOTING_ONLY_VALID_STATES = [
  ReferenceActionState.PENDING_ACTION,
  ReferenceActionState.PENDING_ACTION_BY_YOU,
  ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN,
];

const allowRevokeOutcome = (outcome, currentResearcher) => {
  const allowRevokeResolution = [
    ScreeningOutcomeResolution.UPDATED_OUTCOME,
    ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
  ];
  return (
    outcome?.resolvedBy?.researcher.id === currentResearcher.id &&
    allowRevokeResolution.includes(outcome.resolution)
  );
};

const STATE_TO_PROPS = {
  [ReferenceActionState.PENDING_ACTION]: {
    showVoteButtons: true,
    actionEvent: 'create-vote',
  },
  [ReferenceActionState.PENDING_ACTION_BY_YOU]: {
    showVoteButtons: true,
    labelProp: 'existingVotesLabel',
    actionEvent: 'create-vote',
  },
  [ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN]: {
    showVoteDropdown: true,
    allowRevoke: () => true,
    labelProp: 'existingVotesLabel',
    actionEvent: 'update-vote',
  },
  [ReferenceActionState.PENDING_ACTION_BY_OTHERS]: {
    labelProp: 'existingVotesLabel',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU]: {
    showInclusionButtons: true,
    labelProp: 'conflictLabel',
    actionEvent: 'resolve-conflict',
  },
  [ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS]: {
    labelProp: 'conflictLabel',
  },
  [ReferenceActionState.INCLUDED_BY_CONSENSUS]: {
    showInclusionDropdown: true,
    labelProp: 'consensusLabel',
    actionEvent: 'update-outcome',
  },
  [ReferenceActionState.EXCLUDED_BY_CONSENSUS]: {
    showInclusionDropdown: true,
    labelProp: 'consensusLabel',
    actionEvent: 'update-outcome',
  },
  [ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION]: {
    showInclusionDropdown: true,
    labelProp: 'resolvedConflictLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
    allowRevoke: allowRevokeOutcome,
  },
  [ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION]: {
    showInclusionDropdown: true,
    labelProp: 'resolvedConflictLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
    allowRevoke: allowRevokeOutcome,
  },
  [ReferenceActionState.INCLUDED_BY_OVERRIDE]: {
    showInclusionDropdown: true,
    labelProp: 'manualOverrideLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
    allowRevoke: allowRevokeOutcome,
  },
  [ReferenceActionState.EXCLUDED_BY_OVERRIDE]: {
    showInclusionDropdown: true,
    labelProp: 'manualOverrideLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
    allowRevoke: allowRevokeOutcome,
  },
  [ReferenceActionState.INCLUDED_ON_UPLOAD]: {
    showInclusionDropdown: true,
    labelProp: 'preScreenedLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
  },
  [ReferenceActionState.EXCLUDED_ON_UPLOAD]: {
    showInclusionDropdown: true,
    labelProp: 'preScreenedLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
  },
  [ReferenceActionState.EXCLUDED_BY_SUGGESTION]: {
    showInclusionDropdown: true,
    labelProp: 'excludedBySuggestionLabel',
    showOutcomeResolver: true,
    actionEvent: 'update-outcome',
  },
};

export default {
  name: 'ScreenCardActions',
  components: {
    ResearcherAvatarCluster,
    VoteControls,
    AssessmentDropdown,
    NotRelevantReviewModal,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    votingOnly: {
      type: Boolean,
      default: false,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'create-vote',
    'update-vote',
    'resolve-conflict',
    'update-outcome',
    'revoke-vote',
    'revoke-outcome',
  ],
  data: () => ({
    showExcludeModal: false,
  }),
  computed: {
    votes() {
      return this.fullTextReview
        ? this.reference.fullTextReviewVotes
        : this.reference.reviewerScreeningVotes;
    },
    researchersToShow() {
      if (this.showOutcomeResolver) {
        return [this.outcome.resolvedBy.researcher];
      }
      if (
        this.votingOnly &&
        this.hasVoteFromResearcher(this.currentResearcher)
      ) {
        return [this.currentResearcher];
      }
      return this.votes
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean)
        .slice(0, 3);
    },
    label() {
      return this[this.styleProps.labelProp];
    },
    actionEvent() {
      return this.styleProps.actionEvent;
    },
    showVoteButtons() {
      return this.styleProps.showVoteButtons;
    },
    showVoteDropdown() {
      return this.styleProps.showVoteDropdown;
    },
    allowRevoke() {
      return this.styleProps.allowRevoke?.(
        this.outcome,
        this.currentResearcher
      );
    },
    showInclusionButtons() {
      return this.styleProps.showInclusionButtons;
    },
    showInclusionDropdown() {
      return this.styleProps.showInclusionDropdown;
    },
    disableChangingInclusionOutcome() {
      return this.fullTextReview
        ? !this.reference.isPendingExtraction()
        : !this.reference.isPendingFullTextReview();
    },
    disableChangingInclusionOutcomeTooltip() {
      return this.fullTextReview
        ? 'Data extraction has started for this reference, so you can no longer modify its inclusion state.'
        : 'Full text review has started for this reference, so you can no longer modify its screening state.';
    },
    yourVote() {
      return this.getVoteByResearcher(this.currentResearcher)?.vote;
    },
    showOutcomeResolver() {
      return this.styleProps.showOutcomeResolver;
    },
    votedOnLabel() {
      return this.fullTextReview ? 'reviewed' : 'voted on';
    },
    votesLabel() {
      return this.fullTextReview ? 'reviews' : 'votes';
    },
    screenedLabel() {
      return this.fullTextReview ? 'reviewed' : 'screened';
    },
    existingVotesLabel() {
      return `${this.voterNamesLabel} ${this.votedOnLabel} this.`;
    },
    conflictLabel() {
      return `${this.voterNamesLabel} have conflicting ${this.votesLabel} on this.`;
    },
    resolvedConflictLabel() {
      return `${this.outcomeResolverName} resolved the conflict on this.`;
    },
    consensusLabel() {
      return this.researchersToShow.length > 1
        ? `${this.voterNamesLabel} reached consensus on this.`
        : `${this.voterNamesLabel} ${this.screenedLabel} this.`;
    },
    manualOverrideLabel() {
      return `${this.outcomeResolverName} overrode a previous outcome on this.`;
    },
    preScreenedLabel() {
      return `${this.outcomeResolverName} pre-screened this.`;
    },
    excludedBySuggestionLabel() {
      return `${this.outcomeResolverName} excluded this based on our recommendation.`;
    },
    outcomeResolverName() {
      const reviewMember = this.outcome.resolvedBy;
      const researcher = reviewMember?.researcher;
      return researcher?.id === this.currentResearcher.id
        ? 'You'
        : researcher?.firstName();
    },
    voterNamesLabel() {
      const hasVotesFromYou = this.hasVoteFromResearcher(
        this.currentResearcher
      );
      const otherVoters = this.votes
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean)
        .filter(
          (researcher) => researcher.id !== this.currentResearcher.id
        )
        .slice(0, 3);

      if (hasVotesFromYou) {
        if (this.votingOnly) return 'You';

        return ['You', otherVoters[0]?.firstName()]
          .filter(Boolean)
          .join(' and ');
      } else {
        return otherVoters
          .map((researcher) => researcher.firstName())
          .join(' and ');
      }
    },
    styleProps() {
      return STATE_TO_PROPS[this.actionState];
    },
    actionState() {
      const actionState = this.fullTextReview
        ? this.reference.getFullTextReviewActionState(
            this.review,
            this.currentResearcher
          )
        : this.reference.getScreeningActionState(
            this.review,
            this.currentResearcher
          );

      if (this.votingOnly) {
        return VOTING_ONLY_VALID_STATES.includes(actionState)
          ? actionState
          : ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
      }
      return actionState;
    },
    outcome() {
      return this.fullTextReview
        ? this.reference.fullTextReviewOutcome
        : this.reference.screeningOutcome;
    },
  },
  methods: {
    getVoteByResearcher(researcher) {
      return this.fullTextReview
        ? this.reference.getFullTextReviewVoteBy(researcher)
        : this.reference.getScreeningVoteBy(researcher);
    },
    hasVoteFromResearcher(researcher) {
      return !!this.getVoteByResearcher(researcher);
    },
    onVoteButtonClick(buttonClicked) {
      const voteMap = this.fullTextReview
        ? VOTE_CONTROLS_TO_INCLUSION_ASSESSMENT
        : VOTE_CONTROLS_TO_RELEVANCE_ASSESSMENT;
      this.onVoteDropdownChange(voteMap[buttonClicked]);
    },
    onVoteDropdownChange(vote) {
      if (
        this.fullTextReview &&
        vote === InclusionAssessment.EXCLUDE
      ) {
        this.showExcludeModal = true;
      } else {
        this.$emit(this.actionEvent, vote);
      }
    },
    onExclude({ reviewCriteria, notes }) {
      this.$emit(this.actionEvent, {
        vote: InclusionAssessment.EXCLUDE,
        reviewCriteria,
        notes,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.screen-card-actions {
  display: flex;
  gap: 12px;
  align-items: center;

  .existing-vote-info {
    display: flex;
    align-items: center;
    gap: 8px;

    font-size: 12px;
    color: map-get($grey, 'darken-2');
  }
}
</style>
